import { Grid } from '@mui/material';
import { useState } from 'react';

// Our Components
import CurrencyTextInput from 'components/Input/CurrencyTextInput';
import Dropdown from 'components/Dropdown/Dropdown';
import LenderDropdown from 'components/Dropdown/lender/LenderDropdown';
import LoadingSkeleton from 'components/Frame/LoadingSkelton';
import RateComparisonTable from 'components/DataTable/rates/RateComparisonTable';
import USStateDropdown from 'components/Dropdown/USStateDropdown';
import TextInput from 'components/Input/TextInput';
import { PrimaryButton } from 'components/Button/Button';

// input types
import { NUMBER } from 'components/Input/Types';

// Our Hooks
import useMutateFetchLatestRate from 'hooks/rates/useMutateFetchLatestRate';
import useGetLenderList from 'hooks/lenders/useGetLenderList';

// our utils
import { isSubmissionReady } from 'shared/utils';

// Our Constants
import PROPERTY_TYPES from 'shared/constants/mortgage/propertyTypes';
import OCCUPANCY_TYPES from 'shared/constants/mortgage/occupancyTypes';
import SUPPORTED_LOAN_TERMS from 'shared/constants/mortgage/supportedLoanTerms';
import stateToFipCode from 'shared/constants/mortgage/stateToFipCode';

const breakPoints = {
	xs: 12,
	sm: 6,
	md: 4,
	lg: 3
};

function PylonForm() {
	const [additionalRequestedCash, setAdditionalRequestedCash] = useState('0');
	const [county, setCounty] = useState('');
	const [creditScore, setCreditScore] = useState('');
	const [currentMortgageBalance, setCurrentMortgageBalance] = useState('0');
	const [firstTimeHomeBuyer, setFirstTimeHomeBuyer] = useState('No');
	const [lenders, setLenders] = useState('All');
	const [loanProduct, setLoanProduct] = useState('30 Year Fixed');
	const [monthlyDebt, setMonthlyDebt] = useState('0');
	const [monthlyIncome, setMonthlyIncome] = useState('0');
	const [occupancyType, setOccupancyType] = useState('Primary Residence');
	const [offerMode, setOfferMode] = useState('Purchase');
	const [outOfPocketMax, setOutOfPocketMax] = useState('0');
	const [propertyType, setPropertyType] = useState('Single Family');
	const [propertyValue, setPropertyValue] = useState('');
	const [requestedLoanAmount, setRequestedLoanAmount] = useState('');
	const [selfEmployed, setSelfEmployed] = useState('No');
	const [state, setState] = useState('CA');
	const [zipCode, setZipCode] = useState('');

	const { data: lendersList, isLoading: isLendersLoading } =
		useGetLenderList();

	const {
		mutate: getLatestRate,
		data: offers,
		isLoading: isRateLoading,
		isSuccess: isRateSuccess
	} = useMutateFetchLatestRate();

	const isPurchasedMode = offerMode === 'Purchase';

	const isReady = isPurchasedMode
		? isSubmissionReady([
				requestedLoanAmount,
				propertyValue,
				additionalRequestedCash,
				currentMortgageBalance,
				state,
				county,
				zipCode,
				creditScore,
				propertyType,
				occupancyType,
				loanProduct,
				lenders,
				monthlyDebt,
				monthlyIncome,
				outOfPocketMax
		  ])
		: isSubmissionReady([
				propertyValue,
				currentMortgageBalance,
				state,
				county,
				zipCode,
				creditScore,
				propertyType,
				occupancyType,
				loanProduct,
				lenders,
				monthlyDebt,
				monthlyIncome
		  ]);

	const getAllRates = () => {
		const requestDetails = {
			additionalRequestedCash,
			county,
			creditScore,
			currentMortgageBalance,
			firstTimeHomeBuyer,
			lenders,
			lendersList,
			loanProduct,
			monthlyDebt,
			monthlyIncome,
			occupancyType,
			offerMode,
			outOfPocketMax,
			propertyType,
			propertyValue,
			requestedLoanAmount,
			selfEmployed,
			state,
			zipCode
		};

		getLatestRate(requestDetails);
	};

	if (isLendersLoading)
		return (
			<LoadingSkeleton sx={{ height: 70, width: '100%', marginTop: 2 }} />
		);

	return (
		<Grid container rowSpacing={3} columnSpacing={2}>
			<Grid container item xs={12} rowSpacing={3} columnSpacing={2}>
				<Grid item {...breakPoints}>
					<Dropdown
						label="Offer mode"
						items={['Purchase', 'Refinance']}
						selected={offerMode}
						onChange={setOfferMode}
					/>
				</Grid>
				<Grid item {...breakPoints}>
					<Dropdown
						label="Self Employed"
						items={['Yes', 'No']}
						selected={selfEmployed}
						onChange={setSelfEmployed}
					/>
				</Grid>

				{isPurchasedMode && (
					<Grid item xs={12} sm={6} md={4} lg={3}>
						<Dropdown
							label="First time home buyer"
							items={['Yes', 'No']}
							selected={firstTimeHomeBuyer}
							onChange={setFirstTimeHomeBuyer}
						/>
					</Grid>
				)}
			</Grid>
			{isPurchasedMode && (
				<Grid item {...breakPoints}>
					<CurrencyTextInput
						label="Requested Loan Amount"
						value={requestedLoanAmount}
						onChange={setRequestedLoanAmount}
					/>
				</Grid>
			)}
			<Grid item {...breakPoints}>
				<CurrencyTextInput
					label="Property Value"
					value={propertyValue}
					onChange={setPropertyValue}
					subLabel="sales contract amount for pylon"
				/>
			</Grid>
			<Grid item {...breakPoints}>
				<CurrencyTextInput
					label="Additional Requested Cash"
					value={additionalRequestedCash}
					onChange={setAdditionalRequestedCash}
				/>
			</Grid>

			{!isPurchasedMode && (
				<Grid item {...breakPoints}>
					<CurrencyTextInput
						label="Current Mortgage Balance"
						value={currentMortgageBalance}
						onChange={setCurrentMortgageBalance}
					/>
				</Grid>
			)}

			<Grid item {...breakPoints}>
				<USStateDropdown
					label="State"
					gridColumns={12}
					state={state}
					setState={setState}
				/>
			</Grid>

			<Grid item {...breakPoints}>
				<Dropdown
					label="County"
					items={Object.keys(stateToFipCode[state])}
					selected={county}
					onChange={setCounty}
				/>
			</Grid>

			<Grid item {...breakPoints}>
				<TextInput
					label="Zip Code"
					type={NUMBER}
					value={zipCode}
					onChange={setZipCode}
				/>
			</Grid>

			<Grid item {...breakPoints}>
				<TextInput
					label="Credit Score"
					type={NUMBER}
					value={creditScore}
					onChange={setCreditScore}
				/>
			</Grid>

			<Grid item {...breakPoints}>
				<Dropdown
					label="Property Type"
					items={PROPERTY_TYPES}
					selected={propertyType}
					onChange={setPropertyType}
				/>
			</Grid>

			<Grid item {...breakPoints}>
				<Dropdown
					label="Occupancy Type"
					items={OCCUPANCY_TYPES}
					selected={occupancyType}
					onChange={setOccupancyType}
				/>
			</Grid>

			<Grid item {...breakPoints}>
				<Dropdown
					label="Loan Product"
					items={SUPPORTED_LOAN_TERMS}
					selected={loanProduct}
					onChange={setLoanProduct}
				/>
			</Grid>

			<Grid item {...breakPoints}>
				<LenderDropdown
					selected={lenders}
					onChange={setLenders}
					lendersList={lendersList}
				/>
			</Grid>

			<Grid item {...breakPoints}>
				<CurrencyTextInput
					label="Monthly Debt"
					value={monthlyDebt}
					onChange={setMonthlyDebt}
				/>
			</Grid>

			<Grid item {...breakPoints}>
				<CurrencyTextInput
					label="Monthly Income"
					value={monthlyIncome}
					onChange={setMonthlyIncome}
				/>
			</Grid>

			{isPurchasedMode && (
				<Grid item {...breakPoints}>
					<CurrencyTextInput
						label="Out of pocket max"
						value={outOfPocketMax}
						onChange={setOutOfPocketMax}
						subLabel="Down payment amount"
					/>
				</Grid>
			)}

			<Grid item xs={12}>
				<PrimaryButton
					onClick={() => {
						getAllRates();
					}}
					disabled={!isReady}
				>
					Check Rates
				</PrimaryButton>
			</Grid>
			{isRateLoading && (
				<LoadingSkeleton
					sx={{ height: 517, width: '100%', marginTop: 2 }}
				/>
			)}
			{isRateSuccess && <RateComparisonTable mortechRates={offers} />}
		</Grid>
	);
}

export default PylonForm;
